import { completeHexColor } from '@/lib/color/hexColorInput';
import { SUBTITLE_STROKE_WIDTH_MAX } from '@/lib/subtitles/subtitleOutline';
import type { QuoteSlideBrand } from './buildQuoteSlideProps';
import { QUOTE_SIZE_SCALE_MAX, QUOTE_SIZE_SCALE_MIN } from './quoteTextLayout';
import type { QuoteSlideStyle, QuoteTextStyle } from './types';

export type QuoteTextAlign = 'left' | 'center' | 'right';

/** Resolved text look of a slide (template/style defaults + the carousel's
 *  text style overrides). */
export interface QuoteSlideTypography {
  align: QuoteTextAlign;
  bold: boolean;
  italic: boolean;
  /** 0.7–1.3, multiplies the auto-fit maximum font size. */
  sizeScale: number;
  /** Hard outline drawn outside the glyphs; null = none. */
  outline: { color: string; widthPct: number } | null;
}

/** Black outline width of the 'frame' style quote, % of font size. */
export const FRAME_OUTLINE_WIDTH_PCT = 6;

/** The legacy look of each style: bold, centered, full size; the frame style
 *  carries a black outline, the card style none. */
export function defaultQuoteTypography(style: QuoteSlideStyle): QuoteSlideTypography {
  return {
    align: 'center',
    bold: true,
    italic: false,
    sizeScale: 1,
    outline: style === 'frame' ? { color: '#000000', widthPct: FRAME_OUTLINE_WIDTH_PCT } : null,
  };
}

const ALIGNS = ['left', 'center', 'right'] as const;
const MAX_FONT_FAMILY_CHARS = 200;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));
/** The ColorInput rule: complete #RRGGBB / #RRGGBBAA, lowercased. */
const hex = (value: unknown) => (typeof value === 'string' ? completeHexColor(value) : null);

/**
 * Validates a stored carousel text style, keeping only well-formed fields
 * (unknown keys dropped, numbers clamped, colors per the ColorInput rules,
 * https-only font URL). Returns undefined when nothing valid is left, so an
 * empty override never lands in the draft.
 */
export function sanitizeQuoteTextStyle(raw: unknown): QuoteTextStyle | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const c = raw as Record<string, unknown>;
  const out: QuoteTextStyle = {};

  if (typeof c.fontFamily === 'string' && c.fontFamily.trim()) {
    out.fontFamily = c.fontFamily.trim().slice(0, MAX_FONT_FAMILY_CHARS);
  }
  if ('fontUrl' in c) {
    out.fontUrl = typeof c.fontUrl === 'string' && c.fontUrl.startsWith('https://') ? c.fontUrl : null;
  }
  for (const key of ['textColor', 'accentColor', 'backgroundColor'] as const) {
    const color = hex(c[key]);
    if (color) out[key] = color;
  }
  if (isFiniteNumber(c.sizeScale)) {
    out.sizeScale = clamp(c.sizeScale, QUOTE_SIZE_SCALE_MIN, QUOTE_SIZE_SCALE_MAX);
  }
  if (typeof c.align === 'string' && (ALIGNS as readonly string[]).includes(c.align)) {
    out.align = c.align as QuoteTextStyle['align'];
  }
  if (c.outline !== null && typeof c.outline === 'object') {
    const o = c.outline as Record<string, unknown>;
    const color = hex(o.color);
    if (typeof o.enabled === 'boolean' && color && isFiniteNumber(o.widthPct)) {
      out.outline = { enabled: o.enabled, color, widthPct: clamp(o.widthPct, 0, SUBTITLE_STROKE_WIDTH_MAX) };
    }
  }
  if (typeof c.bold === 'boolean') out.bold = c.bold;
  if (typeof c.italic === 'boolean') out.italic = c.italic;

  // A stray `fontUrl` alone carries no look.
  const meaningful = Object.keys(out).filter((key) => key !== 'fontUrl');
  return meaningful.length > 0 ? out : undefined;
}

/**
 * Template brand + style defaults, overridden field by field by the
 * carousel's text style. A text-style font replaces the template font AND
 * its URL (a catalog id never inherits another font's file).
 */
export function resolveQuoteTextStyle(args: {
  brand: QuoteSlideBrand;
  style: QuoteSlideStyle;
  textStyle: QuoteTextStyle | undefined;
}): { brand: QuoteSlideBrand; typography: QuoteSlideTypography } {
  const { brand, style, textStyle: ts } = args;
  const defaults = defaultQuoteTypography(style);
  if (!ts) return { brand, typography: defaults };

  const fontChanged = !!ts.fontFamily;
  return {
    brand: {
      fontFamily: ts.fontFamily ?? brand.fontFamily,
      fontUrl: fontChanged ? ts.fontUrl ?? null : brand.fontUrl,
      textColor: ts.textColor ?? brand.textColor,
      accentColor: ts.accentColor ?? brand.accentColor,
      backgroundColor: ts.backgroundColor ?? brand.backgroundColor,
    },
    typography: {
      align: ts.align ?? defaults.align,
      bold: ts.bold ?? defaults.bold,
      italic: ts.italic ?? defaults.italic,
      sizeScale: ts.sizeScale ?? defaults.sizeScale,
      outline: ts.outline
        ? (ts.outline.enabled ? { color: ts.outline.color, widthPct: ts.outline.widthPct } : null)
        : defaults.outline,
    },
  };
}
